import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { getFieldGames, getPublicFieldGames, getMapGames } from '../../services/fieldService';
import refereeService from '../../services/refereeService';
import { adaptFieldGamesToMissionHistory } from '../../utils/fieldGameAdapter';
import MissionHistoryList from '../mission/MissionHistoryList';
import MissionHistoryFilters from '../mission/MissionHistoryFilters';
import MissionHistoryPagination from '../mission/MissionHistoryPagination';
import { MissionHistory, MissionHistoryFilters as MissionHistoryFiltersType } from '../../types/missionHistory';

const GameTypeTabs = styled.div`
  display: flex;
  flex-shrink: 0;
  margin-bottom: 15px;
`;

const GameTypeTab = styled.button<{ $active: boolean }>`
  font-family: 'Courier New', monospace;
  font-size: 12px;
  padding: 6px 12px;
  background: ${props => props.$active ? '#00d4ff' : 'rgba(0, 212, 255, 0.1)'};
  color: ${props => props.$active ? '#000000' : '#00d4ff'};
  border: 1px solid #00d4ff;
  cursor: pointer;
  text-transform: uppercase;
  font-weight: bold;
  transition: all 0.3s ease;

  &:first-child {
    border-right: none;
  }

  &:last-child {
    border-left: none;
  }

  &:hover {
    background: ${props => props.$active ? '#00d4ff' : 'rgba(0, 212, 255, 0.2)'};
    box-shadow: 0 0 10px rgba(0, 212, 255, 0.3);
  }
`;

const GamesWrapper = styled.div`
  width: 100%;
`;

const SectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

interface GamesSectionProps {
  fieldId?: string;
  refereeId?: string;
  mapId?: string;
  fieldName?: string;
  refereeName?: string;
  mapName?: string;
  supportedGameTypes?: string[];
  showFilters?: boolean;
  showPagination?: boolean;
  isPublic?: boolean;
  className?: string;
}

const GamesSection: React.FC<GamesSectionProps> = ({
  fieldId,
  refereeId,
  mapId,
  fieldName = 'Unknown Field',
  refereeName = 'Unknown Referee',
  mapName = 'Unknown Map',
  supportedGameTypes = ['airsoft', 'paintball', 'speedball'],
  showFilters = true,
  showPagination = true,
  isPublic = false,
  className
}) => {
  const navigate = useNavigate();
  const [activeGameType, setActiveGameType] = useState<'airsoft' | 'paintball' | 'speedball'>('airsoft');
  const [games, setGames] = useState<MissionHistory[]>([]);
  const [gamesLoading, setGamesLoading] = useState(false);
  const [gamesError, setGamesError] = useState<string | null>(null);

  // Пагинация
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [pageSize] = useState(7);

  // Фильтры
  const [gameFilters, setGameFilters] = useState<MissionHistoryFiltersType>({});

  // Определяем режим работы
  const isFieldMode = !!fieldId;
  const isRefereeMode = !!refereeId;
  const isMapMode = !!mapId;
  const targetId = fieldId || refereeId || mapId;

  const loadGames = async (
    gameType: 'airsoft' | 'paintball' | 'speedball' = 'airsoft',
    filters: MissionHistoryFiltersType = {},
    page: number = 1
  ) => {
    if (!targetId) return;

    try {
      setGamesLoading(true);
      setGamesError(null);

      // Готовим параметры для серверной фильтрации
      const options = {
        page,
        pageSize,
        specificDate: filters.specificDate ? filters.specificDate.toISOString().split('T')[0] : undefined
      };

      let response;

      if (isMapMode) {
        // Загружаем игры карты
        response = await getMapGames(mapId!, gameType, options);
      } else if (isFieldMode) {
        // Загружаем игры поля (публичный или авторизованный API)
        response = isPublic
          ? await getPublicFieldGames(fieldId!, gameType, options)
          : await getFieldGames(fieldId!, gameType, options);
      } else if (isRefereeMode) {
        // Загружаем игры рефери
        response = await refereeService.getRefereeGames(refereeId!, gameType, options);
      } else {
        throw new Error('Either fieldId, refereeId, or mapId must be provided');
      }

      // Адаптируем данные к формату MissionHistory
      console.log('Raw response.games:', response.games);
      console.log('First game fieldName:', response.games[0]?.fieldName);

      const adaptedGames = adaptFieldGamesToMissionHistory(
        response.games,
        isFieldMode ? fieldName : undefined,
        isRefereeMode ? refereeName : undefined
      );

      console.log('Adapted games:', adaptedGames);
      console.log('First adapted game fieldName:', adaptedGames[0]?.fieldName);

      setGames(adaptedGames);
      setTotalCount(response.totalCount);
      setTotalPages(response.totalPages);
      setHasMore(response.hasMore);
      setCurrentPage(response.page);
    } catch (error: any) {
      // Если ошибка 404 или подобная - просто показываем пустой список
      if (error.message?.includes('404') || error.message?.includes('not found')) {
        setGames([]);
        setTotalCount(0);
        setTotalPages(0);
        setHasMore(false);
      } else {
        setGamesError('Failed to load missions');
        console.error('Error loading missions:', error);
      }
    } finally {
      setGamesLoading(false);
    }
  };

  const handleFiltersChange = (newFilters: MissionHistoryFiltersType) => {
    setGameFilters(newFilters);
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
    loadGames(activeGameType, gameFilters, 1);
  };

  const handleClearFilters = () => {
    const emptyFilters: MissionHistoryFiltersType = {};
    setGameFilters(emptyFilters);
    setCurrentPage(1);
    loadGames(activeGameType, emptyFilters, 1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    loadGames(activeGameType, gameFilters, page);
  };

  const handleGameClick = (game: MissionHistory) => {
    navigate(`/missions/${activeGameType}/${game.id}`);
  };

  // Загружаем игры при изменении активного типа игры или ID
  useEffect(() => {
    if (targetId) {
      setCurrentPage(1);
      loadGames(activeGameType, gameFilters, 1);
    }
  }, [activeGameType, fieldId, refereeId, mapId]);

  return (
    <GamesWrapper className={className}>
      {/* Кнопки переключения типов игр */}
      <GameTypeTabs>
        {supportedGameTypes.map(gameType => (
          <GameTypeTab
            key={gameType}
            $active={activeGameType === gameType}
            onClick={() => setActiveGameType(gameType as any)}
          >
            {gameType.charAt(0).toUpperCase() + gameType.slice(1)}
          </GameTypeTab>
        ))}
      </GameTypeTabs>

      <SectionContent>
        {/* Фильтры */}
        {showFilters && (
          <MissionHistoryFilters
            filters={gameFilters}
            onFiltersChange={handleFiltersChange}
            onApplyFilters={handleApplyFilters}
            onClearFilters={handleClearFilters}
          />
        )}

        {/* Список игр */}
        <MissionHistoryList
          missions={games}
          loading={gamesLoading}
          error={gamesError}
          onMissionClick={handleGameClick}
          showResultColumn={false}
          showPlayerFaction={false}
          factionLabel="WIN FACTION"
        />

        {/* Пагинация */}
        {showPagination && !gamesLoading && !gamesError && totalCount > 0 && (
          <MissionHistoryPagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={pageSize}
            hasMore={hasMore}
            onPageChange={handlePageChange}
          />
        )}
      </SectionContent>
    </GamesWrapper>
  );
};

export default GamesSection;
